import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Page1 from "./Page1";
import MarketplacePage from "./MarketplacePage";
import NftBurnReward from "./NftBurnReward";
import LpRewardPage from "./LpRewardPage";
import StakingPage from "./StakingPage";
import TokenPage from "./TokenPage";
import TopAchieverPage from "./TopAchieverPage";
import { WalletProvider, useWallet } from "./shared/WalletContext";

const PAGES = [
  {
    key: "matrix",
    label: "Matrix",
    full: "Matrix Dashboard",
    hint: "Subscriptions, levels & referral tree",
    icon: "🧬",
    color: "#00ff88",
    Component: Page1,
  },
  {
    key: "marketplace",
    label: "Marketplace",
    full: "NFT Marketplace",
    hint: "Trade slots, queue & auto-trade bots",
    icon: "🎨",
    color: "#ff00aa",
    Component: MarketplacePage,
  },
  {
    key: "staking",
    label: "Staking",
    full: "OXY Staking",
    hint: "Lock OXY and claim daily rewards",
    icon: "🔒",
    color: "#00ccff",
    Component: StakingPage,
  },
  {
    key: "token",
    label: "Token",
    full: "OXY Token",
    hint: "Balances, transfers, buy & sell",
    icon: "🪙",
    color: "#45b7d1",
    Component: TokenPage,
  },
  {
    key: "burnReward",
    label: "Burn",
    full: "Burn Reward",
    hint: "Signed trade burning & reward pools",
    icon: "🔥",
    color: "#ff8800",
    Component: NftBurnReward,
  },
  {
    key: "lpReward",
    label: "LP",
    full: "LP Reward",
    hint: "Fee swaps & liquidity top-ups",
    icon: "💧",
    color: "#6be0d7",
    Component: LpRewardPage,
  },
  {
    key: "topAchiever",
    label: "Leaders",
    full: "Top Achiever",
    hint: "Cycle leaderboard & payouts",
    icon: "🥇",
    color: "#ffaa00",
    Component: TopAchieverPage,
  },
];

function Shell() {
  const [currentPage, setCurrentPage] = useState("matrix");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { account, shortAccount, connecting, connect, disconnect } = useWallet();

  const active = PAGES.find((p) => p.key === currentPage) || PAGES[0];
  const ActiveComponent = active.Component;

  // The page picker is driven by React state rather than Bootstrap's JS dropdown,
  // so it cannot be broken by a second copy of Bootstrap registering its own handlers.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const goTo = (key) => {
    setCurrentPage(key);
    setMenuOpen(false);
  };

  return (
    <div
      className="min-vh-100 position-relative"
      style={{
        background: "linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 50%, #16213e 100%)",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      }}
    >
      {/* Ambient background */}
      <div
        className="position-fixed w-100 h-100"
        style={{
          background: `
            radial-gradient(circle at 25% 25%, rgba(0, 204, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(255, 0, 170, 0.10) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(0, 255, 136, 0.08) 0%, transparent 50%)
          `,
          zIndex: 0,
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      />

      {/* Top bar */}
      <nav
        className="navbar fixed-top shadow"
        style={{
          background: "rgba(0, 0, 0, 0.92)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          zIndex: 1050,
          padding: "0.5rem 0",
        }}
      >
        <div className="container-fluid px-3 px-md-4 d-flex align-items-center gap-2">
          <div
            className="navbar-brand fw-bold d-flex align-items-center mb-0"
            style={{
              background: "linear-gradient(135deg, #00ccff, #ff00aa, #00ff88)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontSize: "clamp(1.1rem, 4vw, 1.5rem)",
            }}
          >
            🚀 <span className="ms-2">Oxy</span>
            <span className="d-none d-sm-inline ms-1">Hub</span>
          </div>

          {/* Desktop page picker */}
          <div className="position-relative d-none d-lg-block ms-auto me-2" ref={menuRef}>
            <button
              className="btn px-4 py-2 fw-bold d-flex align-items-center"
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              style={{
                background: `linear-gradient(135deg, ${active.color}, ${active.color}88)`,
                border: "none",
                color: "#04121a",
                borderRadius: "25px",
                fontSize: "0.9rem",
                boxShadow: `0 4px 15px ${active.color}44`,
              }}
            >
              <span className="me-2 fs-5">{active.icon}</span>
              {active.full}
              <span
                className="ms-2"
                style={{
                  display: "inline-block",
                  transition: "transform 0.2s ease",
                  transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  fontSize: "0.7rem",
                }}
              >
                ▼
              </span>
            </button>

            {menuOpen && (
              <ul
                className="list-unstyled shadow-lg position-absolute end-0 mb-0"
                role="menu"
                style={{
                  top: "calc(100% + 8px)",
                  background: "rgba(0, 0, 0, 0.96)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "15px",
                  minWidth: "320px",
                  padding: "0.5rem",
                  zIndex: 1060,
                }}
              >
                {PAGES.map((page) => (
                  <li key={page.key}>
                    <button
                      className="btn w-100 d-flex align-items-center p-3 border-0"
                      role="menuitem"
                      onClick={() => goTo(page.key)}
                      style={{
                        background:
                          currentPage === page.key
                            ? `linear-gradient(135deg, ${page.color}33, ${page.color}14)`
                            : "transparent",
                        color: currentPage === page.key ? page.color : "#fff",
                        borderRadius: "10px",
                        margin: "2px 0",
                        transition: "all 0.25s ease",
                      }}
                    >
                      <span className="fs-4 me-3">{page.icon}</span>
                      <div className="flex-grow-1 text-start">
                        <div className="fw-bold">{page.full}</div>
                        <small className="text-white-50">{page.hint}</small>
                      </div>
                      {currentPage === page.key && (
                        <span
                          className="badge rounded-pill ms-2"
                          style={{ background: page.color, color: "#000" }}
                        >
                          ✓
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Wallet button */}
          <div className="ms-auto ms-lg-0">
            {account ? (
              <button
                className="btn btn-sm px-3 py-2 fw-bold"
                onClick={disconnect}
                title="Click to disconnect"
                style={{
                  background: "rgba(0, 255, 136, 0.15)",
                  border: "1px solid #00ff88",
                  color: "#00ff88",
                  borderRadius: "20px",
                  fontSize: "0.8rem",
                }}
              >
                🔗 {shortAccount}
              </button>
            ) : (
              <button
                className="btn btn-sm px-3 py-2 fw-bold"
                onClick={connect}
                disabled={connecting}
                style={{
                  background: "linear-gradient(135deg, #00ccff, #ff00aa)",
                  border: "none",
                  color: "#04121a",
                  borderRadius: "20px",
                  fontSize: "0.8rem",
                }}
              >
                {connecting ? "Connecting…" : "Connect Wallet"}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Page content */}
      <div
        className="position-relative"
        style={{ paddingTop: "clamp(64px, 10vh, 84px)", paddingBottom: "110px", zIndex: 1 }}
      >
        <ActiveComponent />
      </div>

      {/* Mobile / tablet nav */}
      <div
        className="d-lg-none position-fixed bottom-0 start-0 end-0 pb-2 px-2"
        style={{
          zIndex: 1040,
          background: "linear-gradient(to top, rgba(0, 0, 0, 0.95), transparent)",
        }}
      >
        <div
          className="d-flex rounded-4 p-2 shadow-lg overflow-auto"
          style={{
            background: "rgba(0, 0, 0, 0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            gap: "4px",
          }}
        >
          {PAGES.map((page) => (
            <button
              key={page.key}
              className="btn btn-sm d-flex flex-column align-items-center py-2 px-2 flex-shrink-0"
              onClick={() => goTo(page.key)}
              style={{
                background:
                  currentPage === page.key
                    ? `linear-gradient(135deg, ${page.color}, ${page.color}88)`
                    : "transparent",
                border: "none",
                color: currentPage === page.key ? "#04121a" : "#fff",
                borderRadius: "14px",
                transition: "all 0.25s ease",
                minWidth: "68px",
              }}
            >
              <span className="fs-5">{page.icon}</span>
              <small style={{ fontSize: "0.65rem", opacity: currentPage === page.key ? 1 : 0.7 }}>
                {page.label}
              </small>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MainApp() {
  return (
    <WalletProvider>
      <Shell />
    </WalletProvider>
  );
}
